import { Outlet, useLocation } from 'react-router-dom';
import { useState, useMemo, useRef, useLayoutEffect } from 'react';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { useShop } from '@/hooks/useShop';
import { SUPPORT_CONTACT } from '@/lib/brand';

const PAGE_META: Record<string, { title: string; subtitle?: string }> = {
  '/': { title: 'Dashboard', subtitle: 'Tổng quan hệ thống cầm đồ' },
  '/loans': { title: 'Quản Lý Khoản Vay', subtitle: 'Theo dõi và quản lý tất cả khoản vay' },
  '/loans/create': { title: 'Tạo Khoản Vay Mới', subtitle: 'Tạo hợp đồng cầm đồ mới riêng biệt' },
  '/customers': { title: 'Hồ Sơ Khách Hàng', subtitle: 'Quản lý thông tin khách hàng' },
  '/reports': { title: 'Báo Cáo', subtitle: 'Thống kê và phân tích kinh doanh' },
  '/daily-revenue': { title: 'Doanh Thu Hàng Ngày', subtitle: 'Theo dõi thu nhập từng ngày' },
  '/confiscations': { title: 'Quản Lý Tịch Thu', subtitle: 'Tài sản bị tịch thu do quá hạn' },
  '/vehicle-prices': { title: 'Giá Xe', subtitle: 'Bảng giá tham khảo các loại xe máy' },
  '/staff': { title: 'Quản Lý Nhân Viên', subtitle: 'Tạo và quản lý tài khoản nhân viên' },
  '/activity': { title: 'Lịch Sử Hoạt Động', subtitle: 'Theo dõi mọi thay đổi trong hệ thống' },
  '/settings': { title: 'Cài Đặt', subtitle: 'Cấu hình hệ thống quản lý cầm đồ' },
  '/admin/shops': { title: 'Quản Lý Cửa Hàng', subtitle: 'Thuê bao và gia hạn của các tiệm' },
};

// Banner trạng thái thuê bao — hiện trên mọi trang khi sắp/đã hết hạn
function SubscriptionBanner() {
  const { shop, subscription, loading } = useShop();
  if (loading || !shop) return null;

  if (!subscription.isActive) {
    return (
      <div className="bg-red-500 text-white text-sm px-4 py-2.5 flex items-center justify-center gap-2 text-center">
        <i className="ri-error-warning-line"></i>
        <span>
          Tài khoản đã hết hạn — chỉ xem được dữ liệu, không nhập thêm được.
          {SUPPORT_CONTACT ? ` Liên hệ ${SUPPORT_CONTACT} để gia hạn.` : ' Liên hệ quản trị viên để gia hạn.'}
        </span>
      </div>
    );
  }
  if (subscription.isExpiringSoon) {
    return (
      <div className="bg-amber-400 text-[#1a2332] text-sm px-4 py-2 flex items-center justify-center gap-2 text-center">
        <i className="ri-time-line"></i>
        <span>
          Thuê bao còn {subscription.daysLeft} ngày (hết {new Date(subscription.expiresAt!).toLocaleDateString('vi-VN')}).
          {SUPPORT_CONTACT ? ` Gia hạn: ${SUPPORT_CONTACT}.` : ''}
        </span>
      </div>
    );
  }
  return null;
}

export default function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();
  const contentRef = useRef<HTMLDivElement>(null);

  const meta = useMemo(() => PAGE_META[pathname] || { title: '', subtitle: undefined }, [pathname]);

  // Mượt mà fade-in mỗi khi đổi trang — KHÔNG unmount Outlet
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    // Bắt đầu từ mờ + lệch nhẹ, trước khi browser paint
    el.style.opacity = '0';
    el.style.marginTop = '8px';
    el.style.transition = 'none';
    void el.offsetHeight; // force reflow

    // Transition mượt về hiển thị
    el.style.transition = 'opacity 0.35s cubic-bezier(0.16, 1, 0.3, 1), margin-top 0.35s cubic-bezier(0.16, 1, 0.3, 1)';
    el.style.opacity = '1';
    el.style.marginTop = '0px';
  }, [pathname]);

  return (
    <div className="min-h-screen bg-[#f8f9fb] flex">
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        <TopBar
          title={meta.title}
          subtitle={meta.subtitle}
          onMenuClick={() => setSidebarOpen(true)}
        />
        <SubscriptionBanner />
        <main className="flex-1 p-3 md:p-6">
          <div ref={contentRef}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}